import type { ReactNode } from "react";
import { forwardRef } from "react";

import classNames from "classnames";

import { SupportingText } from "@jti/core-components/components/SupportingText";

import { Label } from "./Label/Label";

import styles from "./FieldWrapper.module.scss";

export type FieldWrapperProps = {
  id: string;
  label?: ReactNode;
  labelId?: string;
  renderLabel?(props: RenderLabelProps): ReactNode;
  renderAdditionalContent?(): ReactNode;
  labelCaption?: React.ReactNode;
  checkable?: boolean;
  isFilled?: boolean;
  required?: boolean;
  error?: string | null;
  controlClass?: string;
  children: React.ReactNode;
  className?: string;
  errorClassName?: string;
  dataTestId?: string;
  hasEmptyError?: boolean;
  showCustomError?: boolean;
  labelClassName?: string;
};

type RenderLabelProps = Pick<FieldWrapperProps, "required" | "checkable" | "id" | "error" | "labelCaption">;
/**
 * @deprecated
 */
export const FieldWrapper = forwardRef<HTMLDivElement, FieldWrapperProps>(
  (
    {
      id,
      label,
      labelId,
      labelCaption,
      checkable,
      isFilled,
      error,
      required,
      children,
      renderLabel,
      renderAdditionalContent,
      className = "",
      controlClass = "",
      errorClassName = "",
      dataTestId = "",
      hasEmptyError,
      showCustomError = false,
      labelClassName,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        data-form-field="true"
        data-testid={dataTestId}
        className={classNames(styles.container, className, {
          [styles.checkable]: checkable,
          [styles.hasError]: error,
          [styles.isFilled]: isFilled,
          [styles.hasEmptyError]: hasEmptyError,
        })}
      >
        {renderLabel ? (
          renderLabel({ required, error, checkable, id, labelCaption })
        ) : (
          <Label
            id={id}
            label={label}
            labelId={labelId}
            required={required}
            checkable={checkable}
            labelCaption={labelCaption}
            labelClassName={labelClassName}
          />
        )}
        <div className={`${styles.control} ${controlClass}`}>
          {children}
          {!error && renderAdditionalContent?.()}
        </div>
        {error && !showCustomError ? (
          <div className={classNames(styles.error, errorClassName)}>
            <SupportingText variant="error">{error}</SupportingText>
          </div>
        ) : null}
      </div>
    );
  }
);
